import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  atualizarConfiguracao,
  criarConfiguracao,
  type Configuracao,
  type ConfiguracaoInput,
} from '@/api/secretario';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { setConfiguracaoAtual } from '@/lib/secretario/configuracaoAtual';

import { SO_MEU, useEscoposDisponiveis } from './useEscoposDisponiveis';
import { WizardActions } from './WizardActions';
import { useWizardSave } from './WizardSaveContext';

interface StepProps {
  config: Configuracao | null;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
  /** De quem é o secretário que se cria, quando já veio escolhido (o seletor da página). */
  escopoInicial?: string | null;
}

// Neutros de propósito: o secretário não marca gênero ao falar de si (REGRA_CONCORDANCIA).
const NOME_PADRAO = 'Assistente do escritório';
const SAUDACAO_PADRAO = 'Olá! Faço o atendimento virtual do escritório. Como posso ajudar?';

/** Passo 1 — identidade do secretário. Cria a configuração se ainda não existir. */
export function IdentidadeStep({ config, onNext, onBack, isFirst, escopoInicial }: StepProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const criando = !config;
  const escopos = useEscoposDisponiveis(criando);
  const [escopoEscolhido, setEscopo] = useState<string | null>(escopoInicial ?? null);
  const escopo =
    escopoEscolhido ??
    (escopos.doEscritorio.length > 0 && (!escopos.pessoal || escopos.doEscritorio.length === 1)
      ? escopos.doEscritorio[0].id
      : SO_MEU);
  const doEscritorio = config ? config.escopo === 'ESCRITORIO' : escopo !== SO_MEU;
  const gere = config?.papel !== 'PARTICIPANTE';

  // Na criação, já preenchidos: o advogado só edita se quiser. Configuração
  // existente fica como foi salva (inclusive sem saudação, se ele a apagou).
  const [nomeExibicao, setNomeExibicao] = useState(config ? (config.nome_exibicao ?? '') : NOME_PADRAO);
  const [saudacao, setSaudacao] = useState(config ? (config.saudacao ?? '') : SAUDACAO_PADRAO);
  const [exigirRevisao, setExigirRevisao] = useState(config?.exigir_revisao_advogado ?? false);
  const [prazoRetorno, setPrazoRetorno] = useState(String(config?.prazo_retorno_horas ?? 24));

  // Fora da faixa que o backend aceita (1 a 168) o valor NÃO vai junto: um 422
  // aqui derruba o save inteiro e leva a identidade com ele — perder trabalho por
  // causa de um campo não relacionado é o que o auto-save existe para evitar.
  const prazoHoras = Number(prazoRetorno);
  const prazoValido = Number.isInteger(prazoHoras) && prazoHoras >= 1 && prazoHoras <= 168;

  const mutation = useMutation({
    mutationFn: () => {
      const payload: ConfiguracaoInput = {
        nome_exibicao: nomeExibicao.trim(),
        saudacao: saudacao.trim() || null,
        // Tom de voz escondido (decisão do Adriano, 07/10/2026): pouca diferença na
        // conversa e uma decisão a mais no assistente. Salvar volta ao estilo padrão,
        // para ninguém ficar com um tom aplicado que não vê nem pode mudar.
        persona: null,
        exigir_revisao_advogado: exigirRevisao,
        ...(prazoValido ? { prazo_retorno_horas: prazoHoras } : {}),
      };
      return criando
        ? criarConfiguracao({ ...payload, escritorio_id: escopo === SO_MEU ? null : escopo })
        : atualizarConfiguracao(payload);
    },
    // Avançar NÃO acontece aqui: o mesmo save roda no auto-save da navegação
    // (flush), que não pode mudar de passo sozinho. O avanço fica no onPrimary.
    onSuccess: (salva) => {
      if (criando) {
        // A tela passa a mostrar o Secretário criado (o seletor e o cache são dele).
        setConfiguracaoAtual(salva.id);
        queryClient.invalidateQueries({ queryKey: queryKeys.secretario.acessiveis });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('Identidade salva.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // Auto-save ao navegar: registra o save enquanto houver edição não salva
  // válida (nunca perder trabalho). Na criação não registra — criar a config é
  // ação expressa do botão, não efeito colateral de navegação.
  const registerFlush = useWizardSave();
  const { mutate, isPending } = mutation;
  const dirty =
    config != null &&
    (nomeExibicao.trim() !== (config.nome_exibicao ?? '') ||
      (saudacao.trim() || null) !== (config.saudacao ?? null) ||
      exigirRevisao !== (config.exigir_revisao_advogado ?? false) ||
      (prazoValido && prazoHoras !== config.prazo_retorno_horas));
  useEffect(() => {
    const salvavel = dirty && !!nomeExibicao.trim() && !isPending;
    registerFlush('identidade', salvavel ? () => mutate() : null);
    return () => registerFlush('identidade', null);
  }, [dirty, nomeExibicao, isPending, mutate, registerFlush]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{doEscritorio ? 'O secretário do escritório' : 'O seu secretário'}</CardTitle>
        <CardDescription>
          {doEscritorio
            ? 'Como ele se apresenta aos clientes do escritório no WhatsApp e até onde vai sozinho.'
            : 'Como ele se apresenta, conversa com os seus clientes no WhatsApp e até onde vai sozinho.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {criando && escopos.doEscritorio.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">De quem é este secretário?</legend>
            <RadioGroup value={escopo} onValueChange={setEscopo} className="gap-2">
              {escopos.doEscritorio.map((e) => (
                <Label
                  key={e.id}
                  htmlFor={`escopo-${e.id}`}
                  className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal"
                >
                  <RadioGroupItem id={`escopo-${e.id}`} value={e.id} className="mt-0.5" />
                  <span>
                    <span className="block font-medium">Do escritório {e.nome}</span>
                    <span className="block text-xs text-muted-foreground">
                      Atende os clientes do escritório em nome dos advogados que você escolher. O add-on é do
                      escritório, na sua assinatura.
                    </span>
                  </span>
                </Label>
              ))}
              {escopos.pessoal && (
                <Label
                  htmlFor="escopo-pessoal"
                  className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal"
                >
                  <RadioGroupItem id="escopo-pessoal" value={SO_MEU} className="mt-0.5" />
                  <span>
                    <span className="block font-medium">Só meu</span>
                    <span className="block text-xs text-muted-foreground">
                      Atende só em seu nome, com os seus clientes. O add-on é seu.
                    </span>
                  </span>
                </Label>
              )}
            </RadioGroup>
          </fieldset>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="nomeExibicao">Nome de exibição</Label>
          <Input
            id="nomeExibicao"
            value={nomeExibicao}
            onChange={(e) => setNomeExibicao(e.target.value)}
            placeholder="Ex.: Assistente do escritório"
            maxLength={120}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="saudacao">Saudação inicial</Label>
          <Textarea
            id="saudacao"
            value={saudacao}
            onChange={(e) => setSaudacao(e.target.value)}
            placeholder="Olá! Faço o atendimento virtual do escritório. Como posso ajudar?"
            rows={2}
          />
        </div>

        {/* Como ele trabalha ---------------------------------------------
            Regras de FUNCIONAMENTO do secretário, não dados do advogado. Elas
            moravam no passo "Seus dados", junto de OAB, certificado e endereço,
            e ali eram duas coisas de natureza diferente na mesma tela. */}
        <div className="space-y-4 border-t pt-4">
          <div>
            <h3 className="text-sm font-medium">Como ele trabalha</h3>
            <p className="text-xs text-muted-foreground">
              Até onde ele vai sozinho, e o que promete ao cliente em seu nome.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label htmlFor="exigirRevisao">Revisar instrumentos antes de enviar</Label>
              <p className="text-xs text-muted-foreground">
                Antes de mandar ao cliente contrato/procuração para assinatura, encaminha para a sua revisão.
              </p>
            </div>
            <Switch id="exigirRevisao" checked={exigirRevisao} onCheckedChange={setExigirRevisao} />
          </div>

          <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Label htmlFor="prazoRetorno">Prazo de retorno ao cliente</Label>
              <p className="text-xs text-muted-foreground">
                Quando ele encaminha um atendimento para você, é este prazo que ele informa ao cliente. Vale
                cadastrar o que você cumpre — a promessa é sua, não dele.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Input
                id="prazoRetorno"
                type="number"
                min={1}
                max={168}
                value={prazoRetorno}
                onChange={(e) => setPrazoRetorno(e.target.value)}
                className="w-20"
                aria-invalid={!prazoValido}
              />
              <span className="text-sm text-muted-foreground">horas</span>
            </div>
          </div>
          {!prazoValido && <p className="text-xs text-destructive">Informe de 1 a 168 horas (uma semana).</p>}
        </div>

        <WizardActions
          isFirst={isFirst}
          onBack={onBack}
          primaryLabel="Salvar e continuar"
          onPrimary={() => mutation.mutate(undefined, { onSuccess: onNext })}
          primaryDisabled={!nomeExibicao.trim() || !gere || (criando && escopos.carregando)}
          loading={mutation.isPending}
        />
      </CardContent>
    </Card>
  );
}
